import PropTypes from 'prop-types';
import { HOME_CSS_VARS, homeToneClass } from './homeTokens';

/**
 * The ONE card of the new home dashboards (KAN-946, final mockup `.jc`):
 * radius 20 (18 phone), padding 20 (16 phone), a 32px header row (icon tile,
 * 16px/600 sentence-case title, optional muted subtitle, `actions` on the right),
 * 16px (14 phone) from header to content. Every card root carries the
 * --cvx-home-* custom properties (homeTokens.js) so no colour literal lives in CSS.
 *
 * `span` (4|5|6|7|8) only matters on web, where the screen's stack
 * (`homeStackProps('web')`) is a 12-column grid. `stretch` makes a list inside
 * share spare height with a taller neighbour. `layout` is 'web' | 'tablet' | 'phone'
 * (use `resolveHomeLayout`); it also keys the card's own touch sizing.
 *
 * The header row is 32px when the title and `actions` fit on one line; when they do not, the
 * row wraps and the actions drop to their own line at the right — the title is never cut. The
 * icon tile and the title stay together (`.cvx-home-id`), so a very long title wraps BESIDE its
 * tile instead of pushing the tile onto a line of its own.
 */
export default function HomeCard({
  title, subtitle, icon: Icon, iconTone = 'brand', actions, stretch = false, span, layout = 'web',
  testId, ariaLabel, className, style, children,
}) {
  const hasHead = Boolean(title || Icon || actions);
  const label = ariaLabel || (typeof title === 'string' ? title : undefined);
  return (
    <section
      className={className ? `cvx-home-card ${className}` : 'cvx-home-card'}
      data-span={span}
      data-home-layout={layout}
      data-stretch={stretch ? '' : undefined}
      data-testid={testId}
      aria-label={label}
      style={style ? { ...HOME_CSS_VARS, ...style } : HOME_CSS_VARS}
    >
      {hasHead ? (
        <header className="cvx-home-head">
          {Icon || title ? (
            <div className="cvx-home-id">
              {Icon ? (
                <span className={`cvx-home-tile ${homeToneClass(iconTone)}`} aria-hidden="true">
                  <Icon size={17} strokeWidth={1.9} aria-hidden />
                </span>
              ) : null}
              {title ? (
                <h2 className="cvx-home-title">
                  {title}
                  {subtitle ? <small>{subtitle}</small> : null}
                </h2>
              ) : null}
            </div>
          ) : null}
          {actions ? <span className="cvx-home-actions">{actions}</span> : null}
        </header>
      ) : null}
      {children}
    </section>
  );
}

HomeCard.propTypes = {
  title: PropTypes.node,
  subtitle: PropTypes.node,
  icon: PropTypes.elementType,
  iconTone: PropTypes.oneOf(['brand', 'green', 'amber', 'red', 'gray', 'navy']),
  actions: PropTypes.node,
  stretch: PropTypes.bool,
  span: PropTypes.oneOf([4, 5, 6, 7, 8, 12]),
  layout: PropTypes.oneOf(['web', 'tablet', 'phone']),
  testId: PropTypes.string,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  children: PropTypes.node,
};
